// Device integration: microphone capture, screen wake lock, fullscreen.

/** Talk mic with echo cancellation / noise suppression: operators may not wear headsets. */
export async function openMic(): Promise<MediaStream> {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('Microphone needs HTTPS: install the talkback certificate on this device');
  }
  // Safari 16.4+: keep output on the speaker and enable voice processing.
  const nav = navigator as Navigator & { audioSession?: { type: string } };
  if (nav.audioSession) nav.audioSession.type = 'play-and-record';
  return navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
      sampleRate: 48000,
    },
    video: false,
  });
}

type Sentinel = { release(): Promise<void>; addEventListener(t: 'release', cb: () => void): void };
let sentinel: Sentinel | null = null;
let wanted = false;

/** Keep the screen on while the operator view is open; re-acquired when the tab becomes visible again. */
export async function keepAwake(on: boolean): Promise<void> {
  wanted = on;
  const wl = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<Sentinel> } }).wakeLock;
  if (!wl) return;
  if (!on) {
    await sentinel?.release().catch(() => undefined);
    sentinel = null;
    return;
  }
  if (sentinel) return;
  try {
    sentinel = await wl.request('screen');
    sentinel.addEventListener('release', () => {
      sentinel = null;
    });
  } catch {
    /* denied (battery saver) or not visible */
  }
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && wanted) void keepAwake(true);
});

export async function toggleFullscreen(): Promise<void> {
  const d = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
  const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
  if (document.fullscreenElement || d.webkitFullscreenElement) {
    if (document.exitFullscreen) await document.exitFullscreen().catch(() => undefined);
    else d.webkitExitFullscreen?.();
  } else if (el.requestFullscreen) {
    await el.requestFullscreen({ navigationUI: 'hide' }).catch(() => undefined);
  } else {
    el.webkitRequestFullscreen?.();
  }
}
